export type PreviewKind = "image" | "tiff" | "heic" | "video" | "text" | "other";

const getExtension = (fileName: string): string => {
    const idx = fileName.lastIndexOf(".");
    if (idx === -1) return "";
    return fileName.slice(idx + 1).toLowerCase();
};

export function isGenericBinaryContentType(contentType: string | null | undefined): boolean {
    if (!contentType) return true;
    const normalized = contentType.split(";")[0].trim().toLowerCase();
    return (
        normalized === "application/octet-stream" ||
        normalized === "binary/octet-stream" ||
        normalized === "application/download" ||
        normalized === "application/force-download"
    );
}

export function getPreviewKindFromFilename(fileName: string | null | undefined): PreviewKind {
    if (!fileName) return "other";
    const ext = getExtension(fileName);
    if (["heic", "heif", "heics", "heifs"].includes(ext)) return "heic";
    if (["tif", "tiff"].includes(ext)) return "tiff";
    if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) return "image";
    if (["mp4", "webm", "ogg", "mov", "m4v"].includes(ext)) return "video";
    if (["txt", "log", "csv", "json", "xml"].includes(ext)) return "text";
    return "other";
}

export function getFilenameFromContentDisposition(contentDisposition: string | null | undefined): string | null {
    if (!contentDisposition) return null;

    // RFC 5987: filename*=UTF-8''...
    const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
    if (filenameStarMatch?.[1]) {
        const raw = filenameStarMatch[1].trim();
        try {
            return decodeURIComponent(raw);
        } catch {
            return raw;
        }
    }

    // Basic: filename="..." or filename=...
    const filenameMatch = contentDisposition.match(/filename\s*=\s*"?([^";\n]+)"?/i);
    return filenameMatch?.[1]?.trim() ?? null;
}

export function getPreviewKindFromContentType(contentType: string | null | undefined): PreviewKind {
    if (!contentType) return "other";

    const normalized = contentType.split(";")[0].trim().toLowerCase();

    if (
        normalized === "image/heic" ||
        normalized === "image/heif" ||
        normalized === "image/heic-sequence" ||
        normalized === "image/heif-sequence"
    )
        return "heic";
    if (normalized === "image/tiff" || normalized === "image/tif") return "tiff";
    if (normalized.startsWith("image/")) return "image";
    if (normalized.startsWith("video/")) return "video";

    if (normalized.startsWith("text/")) return "text";
    if (["application/json", "application/xml", "application/x-ndjson"].includes(normalized)) return "text";

    return "other";
}

export function getPreviewKindFromContentTypeOrFilename(
    contentType: string | null | undefined,
    fileName: string | null | undefined
): PreviewKind {
    const byType = getPreviewKindFromContentType(contentType);
    if (byType !== "other") return byType;

    if (!isGenericBinaryContentType(contentType)) return "other";
    return getPreviewKindFromFilename(fileName);
}
